/**
 * i18n — minimal client-side string lookup.
 *
 * W9-6 bootstrapped this with TR + EN. W10-6 extended to 5 locales:
 *   tr-TR (primary)   — feedback-language-tr.md mixed-mode register
 *   en-US             — fallback for EN-speaking pilot ops
 *   ro-RO (W10-6)     — Trendyol International RO marketplace
 *   el-GR (W10-6)     — Trendyol International GR marketplace
 *   az-AZ (W10-6)     — Trendyol International AZ marketplace (Latin script)
 *
 * The full Tolgee runtime is gated behind T-39 (Wave 7 deferred → Wave 10+).
 * Until then this module provides a flat dot-path lookup against the bundled
 * locale JSONs.
 *
 * Resolution order:
 *   1. explicit `locale` param
 *   2. `localStorage.commerco_locale` (set by `WorkspaceSettingsForm` or
 *      future `WorkspaceSelector` work)
 *   3. `navigator.language` prefix match:
 *        - "tr" → "tr-TR"
 *        - "en" → "en-US"
 *        - "ro" → "ro-RO"
 *        - "el" / "gr" → "el-GR"
 *        - "az" → "az-AZ"
 *   4. fallback "tr-TR" (Commerco TR-primary per feedback-language-tr.md)
 *
 * Missing-key behaviour: tries the current locale first, then EN as the
 * universal fallback, then returns the dotted path itself (e.g.
 * "onboarding.foo") so the developer notices the typo in QA instead of a
 * silent empty string on a customer screen.
 */

import trMessages from "./tr.json" assert { type: "json" };
import enMessages from "./en.json" assert { type: "json" };
import roMessages from "./ro.json" assert { type: "json" };
import elMessages from "./el.json" assert { type: "json" };
import azMessages from "./az.json" assert { type: "json" };

export type SupportedLocale =
  | "tr-TR"
  | "en-US"
  | "ro-RO"
  | "el-GR"
  | "az-AZ";

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = [
  "tr-TR",
  "en-US",
  "ro-RO",
  "el-GR",
  "az-AZ",
] as const;

const MESSAGES: Record<SupportedLocale, unknown> = {
  "tr-TR": trMessages,
  "en-US": enMessages,
  "ro-RO": roMessages,
  "el-GR": elMessages,
  "az-AZ": azMessages,
};

function isSupported(value: string | null | undefined): value is SupportedLocale {
  return (
    value === "tr-TR" ||
    value === "en-US" ||
    value === "ro-RO" ||
    value === "el-GR" ||
    value === "az-AZ"
  );
}

export function detectLocale(): SupportedLocale {
  if (typeof window === "undefined") return "tr-TR";
  const stored = window.localStorage.getItem("commerco_locale");
  if (isSupported(stored)) return stored;
  const nav = window.navigator.language?.toLowerCase() ?? "";
  if (nav.startsWith("tr")) return "tr-TR";
  if (nav.startsWith("en")) return "en-US";
  if (nav.startsWith("ro")) return "ro-RO";
  // navigator.language reports modern Greek as "el" (sometimes "gr" on legacy
  // browsers — accept both prefixes defensively).
  if (nav.startsWith("el") || nav.startsWith("gr")) return "el-GR";
  if (nav.startsWith("az")) return "az-AZ";
  return "tr-TR";
}

/**
 * Resolve a dotted path against the locale bundle.
 *
 * Example: `t("onboarding.welcome.title", "ro-RO")` →
 *          "Bun venit, Selin!"
 *
 * Fallback chain: requested locale → en-US → tr-TR → dotted path.
 * Keeps a single missing key from leaking the raw dotted path to a customer
 * screen when only the EN/TR pool has been backfilled by an agent.
 */
export function t(
  path: string,
  locale: SupportedLocale = "tr-TR",
): string {
  const segments = path.split(".");

  const tryBundle = (bundle: unknown): string | undefined => {
    let cursor: unknown = bundle;
    for (const seg of segments) {
      if (
        cursor != null &&
        typeof cursor === "object" &&
        seg in (cursor as Record<string, unknown>)
      ) {
        cursor = (cursor as Record<string, unknown>)[seg];
      } else {
        return undefined;
      }
    }
    return typeof cursor === "string" ? cursor : undefined;
  };

  const primary = tryBundle(MESSAGES[locale] ?? MESSAGES["tr-TR"]);
  if (primary !== undefined) return primary;
  if (locale !== "en-US") {
    const en = tryBundle(MESSAGES["en-US"]);
    if (en !== undefined) return en;
  }
  if (locale !== "tr-TR") {
    const tr = tryBundle(MESSAGES["tr-TR"]);
    if (tr !== undefined) return tr;
  }
  return path;
}

/** Convenience: returns a `t`-bound function for the given locale. */
export function makeT(locale: SupportedLocale = "tr-TR"): (k: string) => string {
  return (k: string) => t(k, locale);
}
